import StorageEssentialsApi from "@glidevvr/se-api-wrapper";
import {createApi} from "@reduxjs/toolkit/query/react";
import type {RootState} from "@/store";
import type {BaseQueryFn} from "@reduxjs/toolkit/query/react";
import type {UnitGroup, Facility} from "@/types";

interface BaseQueryArgs<T = Record<string, unknown>> {
    method: string;
    params?: T;
}

export interface GetUnitGroupsParams {
    "facility-ids": string;
    group?: string;
    sort?: string;
    "unit-source"?: string;
    width?: number;
    "width-gt"?: number;
    "width-lt"?: number;
    length?: number;
    "length-gt"?: number;
    "length-lt"?: number;
    categories?: string;
    features?: string;
    strict?: boolean;
    "min-price"?: boolean;
    "unit-id"?: number;
}

interface ResponseMeta {
    execution_time: number;
    count: number;
}

type GetUnitGroupsResponse = { data: UnitGroup[]; meta: ResponseMeta };

interface GetFacilitiesParams {
    location?: string;
    radius?: number;
}

type GetFacilitiesResponse = { data: Facility[]; meta: ResponseMeta };

interface GetFacilitiesV2Params {
    ids?: string;
    states?: string;
    cities?: string;
    zipcodes?: string;
    source?: string;
    lat?: number;
    lng?: number;
    distance?: number;
    unit?: "mi" | "km",
    withSettings?: boolean;
}

export type GetFacilitiesV2Response = { data: Facility[]; meta: ResponseMeta };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const seApiBaseQuery: BaseQueryFn<BaseQueryArgs<any>, unknown, unknown> = async (
    {method, params},
    api,
) => {
    try {
        const state = api.getState() as RootState;
        const apiKey = state.apiKey.apiKey;

        if (!apiKey) {
            throw new Error("API key is not available");
        }
        // Instantiate your API wrapper
        
        const environment = import.meta.env.VITE_SE_ENVIRONMENT;
        const seApi = new StorageEssentialsApi(apiKey, environment);

        if (!seApi[method]) {
            throw new Error(
                `Method ${method} is not found on the API instance`,
            );
        }

        // Dynamically call the method on the API wrapper
        const {data} = await seApi[method](params);

        // Return the data in the expected format
        return {data};
    } catch (error) {
        const serializedError = {
            message: error instanceof Error ? error.message : 'Unknown error occurred',
            name: error instanceof Error ? error.name : 'UnknownError',
        };
        console.error('API Error: ', serializedError);
        return {error: serializedError};
    }
};

// Define your API slice
export const seApi = createApi({
    reducerPath: "seApi",
    baseQuery: seApiBaseQuery,
    endpoints: (builder) => ({
        getUnitGroups: builder.query<GetUnitGroupsResponse, GetUnitGroupsParams>({
            query: (params) => ({
                method: "getUnitGroups",
                params,
            }),
            transformErrorResponse: (response: { status: number; data: { message?: string } }) => ({
                status: response.status,
                message: response.data?.message || "An Error Occurred",
                // fullError: response
            }),
        }),
        getFacilities: builder.query<GetFacilitiesResponse, GetFacilitiesParams>({
            query: (params) => ({
                method: "getFacilities",
                params,
            }),
        }),
        getFacilitiesV2: builder.query<GetFacilitiesV2Response, GetFacilitiesV2Params>({
            query: (params) => ({
                method: "getFacilitiesV2",
                params,
            }),
        }),
    }),
});

// Export hooks for your components to use
export const {
    useGetUnitGroupsQuery,
    useGetFacilitiesQuery,
    useGetFacilitiesV2Query,
} = seApi;
